import { useMemo, useState } from 'react'
import { Alert, App, Button, Form, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, getCurrencyCode } from '@/shared/lib/format'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { fetchAllRoles } from '@/features/identity/identityApi'
import {
  BASIS_POINTS,
  deleteDiscountPolicy,
  fetchDiscountPolicies,
  saveDiscountPolicy,
  type DiscountPolicy,
} from '@/features/cashdesk/salesApi'

interface PolicyForm {
  roleId?: string
  maxPercent?: number | null
  maxAmount?: Money | null
  approvalAbove: boolean
}

export function DiscountPoliciesPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<PolicyForm>()
  const [editing, setEditing] = useState<DiscountPolicy | 'new' | null>(null)
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const policies = useQuery({
    queryKey: queryKeys.cashdesk.discountPolicies,
    queryFn: ({ signal }) => fetchDiscountPolicies(signal),
  })
  const roles = useQuery({
    queryKey: queryKeys.identity.roles,
    queryFn: fetchAllRoles,
    enabled: can('role.read'),
    staleTime: 5 * 60_000,
  })
  const roleName = useMemo(() => {
    const map = new Map((roles.data ?? []).map((role) => [role.id, role.name]))
    return (id: string) => map.get(id) ?? id
  }, [roles.data])

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.discountPolicies })

  const save = useMutation({
    mutationFn: (values: PolicyForm) =>
      saveDiscountPolicy({
        roleId: editing !== null && editing !== 'new' ? editing.roleId : (values.roleId ?? ''),
        maxPercentBp:
          values.maxPercent === null || values.maxPercent === undefined
            ? null
            : Math.round(values.maxPercent * 100),
        maxAmount: values.maxAmount ? values.maxAmount.amount : null,
        approvalAbove: values.approvalAbove,
      }),
    onSuccess: () => {
      invalidate()
      setEditing(null)
    },
  })

  const remove = useMutation({ mutationFn: deleteDiscountPolicy, onSuccess: invalidate })

  const open = (target: DiscountPolicy | 'new') => {
    save.reset()
    setEditing(target)
    form.setFieldsValue(
      target === 'new'
        ? { roleId: undefined, maxPercent: null, maxAmount: null, approvalAbove: true }
        : {
            roleId: target.roleId,
            maxPercent: target.maxPercentBp === null ? null : target.maxPercentBp / 100,
            maxAmount: target.maxAmount === null ? null : money(target.maxAmount, currency),
            approvalAbove: target.approvalAbove,
          },
    )
  }

  const confirmRemove = (policy: DiscountPolicy) => {
    modal.confirm({
      title: t('discounts.policy.deleteTitle', { role: roleName(policy.roleId) }),
      content: t('discounts.policy.deleteWarn'),
      okText: t('discounts.policy.delete'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => remove.mutate(policy.roleId),
    })
  }

  const taken = new Set((policies.data ?? []).map((p) => p.roleId))

  const columns: ColumnsType<DiscountPolicy> = [
    { key: 'role', title: t('discounts.policy.role'), render: (_, row) => roleName(row.roleId) },
    {
      key: 'maxPercent',
      title: t('discounts.policy.maxPercent'),
      width: 150,
      align: 'right',
      render: (_, row) =>
        row.maxPercentBp === null ? t('discounts.policy.noCap') : `${(row.maxPercentBp / 100).toFixed(2)}%`,
    },
    {
      key: 'maxAmount',
      title: t('discounts.policy.maxAmount'),
      width: 170,
      align: 'right',
      render: (_, row) =>
        row.maxAmount === null ? t('discounts.policy.noCap') : formatMoneyValue(money(row.maxAmount, currency)),
    },
    {
      key: 'approvalAbove',
      title: t('discounts.policy.aboveLimit'),
      width: 220,
      render: (_, row) =>
        row.approvalAbove ? (
          <Tag color="gold">{t('discounts.policy.aboveApproval')}</Tag>
        ) : (
          <Tag color="red">{t('discounts.policy.aboveForbidden')}</Tag>
        ),
    },
    {
      key: 'updated',
      title: t('discounts.policy.updated'),
      width: 240,
      render: (_, row) =>
        row.updatedBy ? `${row.updatedBy.name} · ${formatDateTime(row.updatedAt)}` : formatDateTime(row.updatedAt),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            { key: 'edit', label: t('common.edit'), icon: <EditOutlined />, onClick: () => open(row) },
            {
              key: 'delete',
              label: t('discounts.policy.delete'),
              icon: <DeleteOutlined />,
              danger: true,
              divider: true,
              onClick: () => confirmRemove(row),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.discountPolicies')}
        description={t('pageIntro.discountPolicies')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            {t('discounts.policy.add')}
          </Button>
        }
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('discounts.policy.rule')} />
        {policies.isError ? <Alert type="error" showIcon message={translateError(policies.error, t)} /> : null}
        {remove.isError ? <Alert type="error" showIcon message={translateError(remove.error, t)} /> : null}
        <Table<DiscountPolicy>
          rowKey={(row) => row.roleId}
          loading={policies.isPending}
          dataSource={policies.data ?? []}
          columns={columns}
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{ emptyText: t('discounts.policy.empty') }}
        />
      </Space>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? t('discounts.policy.addTitle') : t('discounts.policy.editTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={save.isPending}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
          <Form<PolicyForm> form={form} layout="vertical" requiredMark preserve={false}>
            <Form.Item
              name="roleId"
              label={t('discounts.policy.role')}
              rules={[{ required: true, message: t('discounts.policy.roleRequired') }]}
            >
              <Select<string>
                showSearch
                optionFilterProp="label"
                disabled={editing !== 'new'}
                loading={roles.isPending}
                options={(roles.data ?? [])
                  .filter((role) => editing !== 'new' || !taken.has(role.id))
                  .map((role) => ({ value: role.id, label: role.name }))}
              />
            </Form.Item>
            <Form.Item name="maxPercent" label={t('discounts.policy.maxPercent')} extra={t('discounts.policy.capHint')}>
              <InputNumber min={0} max={BASIS_POINTS / 100} step={0.5} precision={2} addonAfter="%" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxAmount" label={t('discounts.policy.maxAmount')} extra={t('discounts.policy.capHint')}>
              <MoneyInput currency={currency} minMinor={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="approvalAbove" label={t('discounts.policy.aboveLimit')} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Typography.Text type="secondary">{t('discounts.policy.aboveHint')}</Typography.Text>
          </Form>
        </Space>
      </Modal>
    </>
  )
}
